import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import apiServices from '../../services/service';
import { updateUserDetail } from '../../redux/reducer';
import { getUserList } from '../../redux/selector';
import { fetchDataAndUpdateStore } from '../../redux/actions';
import PersonalSettings from './personalSettings';
import AuditLog from './auditLog';
import TeamManagement from '../teamManagement/teamManagement';
import BucketManagement from '../bucketManagement/bucketManagement';
import BucketList from '../BucketList/bucketList';
import ActionPermissionList from '../actionPermission/actionPermissionList';
import UiPermissionsList from '../uiPermissions/uiPermissionList';
import RoleManagement from '../roleManagement/roleManagement';
import UserManagementList from '../userManagement/userManagementList';

// built-in role that is always full-access and read-only in this UI (matches the design's locked Administrator)
const SYSTEM_ROLE_NAMES = new Set(['admin', 'administrator']);
const isSystemRole = (role) => !!role && SYSTEM_ROLE_NAMES.has(String(role.name || '').trim().toLowerCase());
// per-role descriptions (real roles carry no description field) — known names + a generic fallback
const ROLE_DESC = {
  admin: 'Unrestricted access to every record and all workspace settings.',
  administrator: 'Unrestricted access to every record and all workspace settings.',
  'system manager': 'Manages workspace configuration, users, and permissions.',
  'sales manager': 'Oversees a team with full control of team records and reporting.',
  'account executive': 'Works owned and team-shared deals through the sales cycle.',
  'sales development rep': 'Prospects and qualifies new leads before handoff.',
  user: 'Standard access for day-to-day CRM users.',
  'read-only': 'View-only access for stakeholders and observers.',
};
const roleDescOf = (role) =>
  (role && ROLE_DESC[String(role.name || '').trim().toLowerCase()]) ||
  'Controls record access and UI visibility for users assigned this role.';

// matrix columns → real UI-permission fields (Export has no backend equivalent)
const UI_FIELD = { view: 'canView', create: 'canCreate', edit: 'canEdit', delete: 'canDelete', export: null };
const ROLE_DOT_COLORS = ['#c0473f', '#6a4fae', '#c96342', '#2e63a6', '#2c7a45', '#9a751c', '#b34480', '#54738f'];
const moduleIconName = (name) => {
  const n = String(name || '').toLowerCase();
  if (n.includes('lead')) return 'leads';
  if (n.includes('contact')) return 'contacts';
  if (n.includes('account')) return 'accounts';
  if (n.includes('campaign') || n.includes('report')) return 'chart';
  if (n.includes('opportunit') || n.includes('deal')) return 'deal';
  if (n.includes('user')) return 'user';
  return 'settings';
};
const applyRolePerm = (node, perm) => {
  node.canView = !!perm?.canView;
  node.canCreate = !!perm?.canCreate;
  node.canEdit = !!perm?.canEdit;
  node.canDelete = !!perm?.canDelete;
};
const topMenuOf = (mod) =>
  mod?.menuPermissions?.find((m) => m.permissionParentId === null) || mod?.menuPermissions?.[0] || null;
// build the saveUiPermissionsByRole payload from the merged tree
const buildUiSaveFormat = (tree, roleId) => {
  const roleAndUiPermissions = [];
  Object.values(tree || {}).forEach((mod) => {
    (mod.menuPermissions || []).forEach((menu) => {
      roleAndUiPermissions.push({ roleId, uiPermissionId: menu.permissionId, canCreate: !!menu.canCreate, canDelete: !!menu.canDelete, canEdit: !!menu.canEdit, canView: !!menu.canView });
      (menu.screenPermissions || []).forEach((sc) => {
        roleAndUiPermissions.push({ roleId, uiPermissionId: sc.permissionId, canCreate: true, canDelete: true, canEdit: true, canView: !!sc.canView });
        (sc.fieldPermissions || []).forEach((f) => {
          roleAndUiPermissions.push({ roleId, uiPermissionId: f.permissionId, canCreate: true, canDelete: true, canEdit: !!f.canEdit, canView: !!f.canView });
        });
      });
    });
  });
  return { roleUiPermissionsVm: { roleAndUiPermissions } };
};

const ICON_PATHS = {
  accounts: 'M3 21h18M5 21V7l7-4 7 4v14M9 9h.01M15 9h.01M9 13h.01M15 13h.01M9 17h6v4H9z',
  bell: 'M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9ZM13.7 21a2 2 0 0 1-3.4 0',
  chart: 'M3 3v18h18M8 14v4M13 9v9M18 5v13',
  check: 'M20 6 9 17l-5-5',
  chevD: 'M6 9l6 6 6-6',
  clock: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 7v5l3 2',
  contacts: 'M4 4h16a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1ZM10 11a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM6 16c.5-1.8 2.2-3 4-3s3.5 1.2 4 3M16 9h3M16 13h3',
  deal: 'M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
  download: 'M12 3v12M7 10l5 5 5-5M5 21h14',
  edit: 'M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z',
  globe: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM2 12h20M12 2a15 15 0 0 1 0 20 15 15 0 0 1 0-20Z',
  image: 'M3 5h18a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1ZM8.5 11a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3ZM21 16l-5-5L5 19',
  inbox: 'M22 12h-6l-2 3h-4l-2-3H2M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11Z',
  info: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 16v-4M12 8h.01',
  leads: 'M16 19v-1a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v1M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM22 19v-1a4 4 0 0 0-3-3.87M16 3.13A4 4 0 0 1 16 11',
  link: 'M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1.5 1.5M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1.5-1.5',
  list: 'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01',
  lock: 'M5 11h14a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1ZM8 11V7a4 4 0 0 1 8 0v4',
  plus: 'M12 5v14M5 12h14',
  search: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM21 21l-4.3-4.3',
  settings: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1V21a2 2 0 0 1-4 0v-.1A1.6 1.6 0 0 0 7 19.4a1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0-1.1-2.7H1a2 2 0 0 1 0-4h.1A1.6 1.6 0 0 0 2.6 7a1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H7a1.6 1.6 0 0 0 1-1.5V1a2 2 0 0 1 4 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V7a1.6 1.6 0 0 0 1.5 1H23a2 2 0 0 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1Z',
  trash: 'M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0v14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V6h12M10 11v6M14 11v6',
  upload: 'M12 21V9M7 14l5-5 5 5M5 3h14',
  user: 'M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z',
  x: 'M18 6 6 18M6 6l12 12',
  zap: 'M13 2 4 14h7l-1 8 9-12h-7l1-8Z',
};

const ACTIONS = ['view', 'create', 'edit', 'delete', 'export'];
const ACTION_LABEL = { view: 'View', create: 'Create', edit: 'Edit', delete: 'Delete', export: 'Export' };
const P = (view, create, edit, del, exp) => ({ view, create, edit, delete: del, export: exp });
const ALL = P(1, 1, 1, 1, 1);
const VIEW = P(1, 0, 0, 0, 0);
const NONE = P(0, 0, 0, 0, 0);
const VCE = P(1, 1, 1, 0, 1);
const VCE_NO_EXPORT = P(1, 1, 1, 0, 0);
const VC = P(1, 1, 0, 0, 0);

const createRoles = () => [
  {
    id: 'admin',
    name: 'Administrator',
    color: '#c0473f',
    system: true,
    desc: 'Unrestricted access to every record and all workspace settings.',
    modules: { leads: ALL, contacts: ALL, accounts: ALL, campaigns: ALL, reports: ALL, settings: ALL },
    scope: 'all',
    caps: { users: 1, billing: 1, integrations: 1, audit: 1, bulkexport: 1 },
  },
  {
    id: 'manager',
    name: 'Sales Manager',
    color: '#6a4fae',
    desc: 'Oversees a team with full control of team records and performance reporting.',
    modules: { leads: ALL, contacts: ALL, accounts: VCE, campaigns: VCE, reports: P(1, 0, 0, 0, 1), settings: VIEW },
    scope: 'team',
    caps: { users: 1, billing: 0, integrations: 0, audit: 1, bulkexport: 1 },
  },
  {
    id: 'ae',
    name: 'Account Executive',
    color: 'var(--gos-accent)',
    desc: 'Works owned and team-shared deals through the full sales cycle.',
    modules: { leads: VCE, contacts: VCE, accounts: VCE_NO_EXPORT, campaigns: VC, reports: VIEW, settings: NONE },
    scope: 'team',
    caps: { users: 0, billing: 0, integrations: 0, audit: 0, bulkexport: 0 },
  },
  {
    id: 'sdr',
    name: 'Sales Development Rep',
    color: '#2e63a6',
    desc: 'Prospects and qualifies new leads before handing off to an AE.',
    modules: { leads: VCE_NO_EXPORT, contacts: VC, accounts: VIEW, campaigns: VIEW, reports: NONE, settings: NONE },
    scope: 'own',
    caps: { users: 0, billing: 0, integrations: 0, audit: 0, bulkexport: 0 },
  },
  {
    id: 'readonly',
    name: 'Read-only',
    color: '#8b9c9c',
    desc: 'View-only access for stakeholders and observers.',
    modules: { leads: VIEW, contacts: VIEW, accounts: VIEW, campaigns: VIEW, reports: VIEW, settings: NONE },
    scope: 'team',
    caps: { users: 0, billing: 0, integrations: 0, audit: 0, bulkexport: 0 },
  },
];

const MEMBERS_INIT = [
  { id: 'u1', name: 'Jordan Wells', email: 'jordan@growthos.com', role: 'admin', status: 'active', last: 'Active now', team: 'Executive', color: '#c0473f' },
  { id: 'u2', name: 'Anil Rao', email: 'anil@growthos.com', role: 'manager', status: 'active', last: 'Active now', team: 'West Region', color: 'var(--gos-accent)' },
  { id: 'u3', name: 'Sara Lin', email: 'sara@growthos.com', role: 'manager', status: 'active', last: '5 min ago', team: 'East Region', color: '#2e63a6' },
  { id: 'u4', name: 'Maya Patel', email: 'maya@growthos.com', role: 'ae', status: 'active', last: '1 hour ago', team: 'Enterprise', color: '#6a4fae' },
  { id: 'u5', name: 'Diego Ramos', email: 'diego@growthos.com', role: 'ae', status: 'active', last: '2 hours ago', team: 'East Region', color: '#b34480' },
  { id: 'u6', name: 'John Smith', email: 'john@growthos.com', role: 'sdr', status: 'active', last: 'Yesterday', team: 'Central Region', color: '#9a751c' },
  { id: 'u7', name: 'Priya Shah', email: 'priya@growthos.com', role: 'sdr', status: 'invited', last: 'Invited Jun 5', team: 'West Region', color: '#2c7a45' },
  { id: 'u8', name: 'Tom Becker', email: 'tom@growthos.com', role: 'readonly', status: 'active', last: '3 days ago', team: 'Solutions', color: '#54738f' },
];

const TEAMS_INIT = [
  {
    id: 'west',
    name: 'West Region',
    color: 'var(--gos-accent)',
    manager: { name: 'Anil Rao', role: 'Sales Manager', color: 'var(--gos-accent)' },
    members: [
      { name: 'Maya Patel', role: 'Account Executive', color: '#6a4fae' },
      { name: 'Priya Shah', role: 'SDR', color: '#2c7a45' },
    ],
  },
  {
    id: 'east',
    name: 'East Region',
    color: '#2e63a6',
    manager: { name: 'Sara Lin', role: 'Sales Manager', color: '#2e63a6' },
    members: [{ name: 'Diego Ramos', role: 'Account Executive', color: '#b34480' }],
  },
  {
    id: 'central',
    name: 'Central Region',
    color: '#9a751c',
    manager: { name: 'John Smith', role: 'SDR Lead', color: '#9a751c' },
    members: [],
  },
  {
    id: 'ent',
    name: 'Enterprise',
    color: '#6a4fae',
    manager: { name: 'Maya Patel', role: 'Enterprise AE', color: '#6a4fae' },
    members: [{ name: 'Tom Becker', role: 'Solutions', color: '#54738f' }],
  },
];

const TERRITORIES = [
  { id: 't1', name: 'Western US', region: 'West', color: 'var(--gos-accent)', team: 'West Region', lead: 'Anil Rao', leadColor: 'var(--gos-accent)', accounts: 142, states: 'CA, WA, OR, NV, AZ' },
  { id: 't2', name: 'Eastern US', region: 'East', color: '#2e63a6', team: 'East Region', lead: 'Sara Lin', leadColor: '#2e63a6', accounts: 118, states: 'NY, MA, FL, GA, NC' },
  { id: 't3', name: 'Central US', region: 'Central', color: '#9a751c', team: 'Central Region', lead: 'John Smith', leadColor: '#9a751c', accounts: 76, states: 'IL, TX, CO, MN' },
  { id: 't4', name: 'Enterprise Named', region: 'Global', color: '#6a4fae', team: 'Enterprise', lead: 'Maya Patel', leadColor: '#6a4fae', accounts: 47, states: 'Named accounts, all regions' },
];

const SCOPES = [
  { id: 'own', label: 'Own records only', desc: 'Only records the user owns', icon: 'user' },
  { id: 'team', label: 'Team records', desc: 'Records owned by their team', icon: 'leads' },
  { id: 'territory', label: 'Territory', desc: 'Records within their territory', icon: 'globe' },
  { id: 'all', label: 'All records', desc: 'Every record across the org', icon: 'accounts' },
];

const ADMIN_CAPS = [
  { id: 'users', label: 'Manage users and roles', desc: 'Invite, edit, and deactivate members', icon: 'leads' },
  { id: 'billing', label: 'Manage billing', desc: 'View invoices and change the plan', icon: 'deal' },
  { id: 'integrations', label: 'Manage integrations', desc: 'Connect and configure external apps', icon: 'zap' },
  { id: 'audit', label: 'View audit log', desc: 'See all account activity history', icon: 'clock' },
  { id: 'bulkexport', label: 'Bulk data export', desc: 'Export entire datasets to CSV', icon: 'download' },
];

const NAV = [
  { group: 'Workspace' },
  { id: 'general', label: 'General', icon: 'settings' },
  { id: 'permissions', label: 'Teams & Permissions', icon: 'leads' },
  { id: 'branding', label: 'Branding', icon: 'image', soon: true },
  { group: 'Account' },
  { id: 'personal', label: 'Personal Settings', icon: 'user' },
  { id: 'notifications', label: 'Notifications', icon: 'bell', soon: true },
  { id: 'integrations', label: 'Integrations', icon: 'zap', soon: true },
  { group: 'Organization' },
  { id: 'users', label: 'Users', icon: 'leads' },
  { id: 'rolemgmt', label: 'Role Management', icon: 'settings' },
  { id: 'teams', label: 'Team Management', icon: 'accounts' },
  { id: 'buckets', label: 'Bucket Management', icon: 'inbox' },
  { id: 'bucketlist', label: 'Bucket List', icon: 'list' },
  { id: 'uiperms', label: 'UI Permissions', icon: 'link' },
  { id: 'actionperms', label: 'Action Permissions', icon: 'lock' },
  { label: 'Templates', icon: 'image', path: '/settings/templates' },
  { id: 'audit', label: 'Audit Log', icon: 'clock' },
];

function Icon({ name, size = 18, className }) {
  const d = ICON_PATHS[name];
  if (!d) return null;

  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}

function Avatar({ name, color, size = 36 }) {
  const initials = (name || '?')
    .split(' ')
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();

  return (
    <span className="gos-set-avatar" style={{ width: size, height: size, background: color, fontSize: size * 0.36 }}>
      {initials}
    </span>
  );
}

function Switch({ on, disabled, onClick }) {
  return (
    <button
      type="button"
      className={`gos-set-switch${on ? ' on' : ''}${disabled ? ' disabled' : ''}`}
      onClick={disabled ? undefined : onClick}
      aria-pressed={!!on}
      disabled={disabled}
    />
  );
}

function RoleName({ roleId, roles }) {
  const role = roles.find((item) => item.id === roleId) || roles[0];
  return (
    <span className="gos-set-role-pill">
      <span className="gos-set-role-dot" style={{ background: role.color }} />
      {role.name}
    </span>
  );
}

function GeneralSection() {
  const fields = [
    ['Workspace name', 'GrowthOS'],
    ['Workspace URL', 'app.growthos.com'],
    ['Default timezone', 'PST - GMT-8'],
    ['Fiscal year start', 'January'],
  ];

  return (
    <section className="gos-set-fade">
      <header className="gos-set-head">
        <div>
          <h1>General</h1>
          <p>Workspace name, URL, and regional defaults.</p>
        </div>
      </header>

      <div className="gos-set-card gos-set-card-pad gos-set-general-card">
        <div className="gos-set-form-grid">
          {fields.map(([label, value]) => (
            <label className="gos-set-field" key={label}>
              <span>{label}</span>
              <input defaultValue={value} />
            </label>
          ))}
        </div>

        <div className="gos-set-divider" />

        <div className="gos-set-row spread center">
          <div>
            <div className="gos-set-strong">Workspace logo</div>
            <div className="gos-set-muted">PNG or SVG, at least 256 x 256px.</div>
          </div>
          <button type="button" className="gos-set-btn ghost small">
            <Icon name="upload" size={14} />
            Upload
          </button>
        </div>

        <div className="gos-set-actions">
          <button type="button" className="gos-set-btn ghost">
            Discard
          </button>
          <button type="button" className="gos-set-btn primary">
            <Icon name="check" size={16} />
            Save Changes
          </button>
        </div>
      </div>
    </section>
  );
}

function RolesTab() {
  const dispatch = useDispatch();
  const accountDetail = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('userDetail')); } catch { return null; }
  }, []);

  const [roles, setRoles] = useState([]); // [{id,name,color}] from getRoleList
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [catalogue, setCatalogue] = useState(null); // {module: {menuPermissions:[tree]}}
  const [modules, setModules] = useState([]); // module names (matrix rows)
  const [tree, setTree] = useState(null); // merged tree for the selected role
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isRoleChange, setIsRoleChange] = useState(false);
  // Record visibility + Administration have no backend endpoint — visual-only, kept per-role locally.
  const [scopeByRole, setScopeByRole] = useState({});
  const [capsByRole, setCapsByRole] = useState({});

  // real users → member count per role (each user has roles: [{ id }])
  const userList = useSelector(getUserList);
  useEffect(() => {
    if (!userList || userList.length === 0) dispatch(fetchDataAndUpdateStore({ key: 'userList' }));
  }, [dispatch]);
  const memberCount = (roleId) =>
    (userList || []).filter((u) => (u.roles || []).some((r) => r.id === roleId)).length;

  const selectedRole = roles.find((r) => r.id === selectedRoleId);
  const locked = isSystemRole(selectedRole); // system role → read-only in this UI

  /* roles */
  useEffect(() => {
    apiServices
      .getRoleList()
      .then((res) => {
        const list = (res.data?.rolesList || [])
          .map((r) => ({ id: r.id, name: r.roleName }))
          .sort((a, b) => a.name.localeCompare(b.name)) // A–Z
          .map((r, i) => ({ ...r, color: ROLE_DOT_COLORS[i % ROLE_DOT_COLORS.length] }));
        setRoles(list);
        if (list.length) setSelectedRoleId((prev) => prev ?? list[0].id);
      })
      .catch((e) => console.error('getRoleList', e));
  }, []);

  /* UI-permission catalogue (menu → screen → field tree per module) */
  useEffect(() => {
    apiServices
      .getUiPermissions()
      .then((res) => {
        const data = res.data?.uiPermissionList || [];
        setModules([...new Set(data.map((p) => p.moduleName))]);
        const formatted = {};
        data.forEach((perm) => {
          const m = perm.moduleName;
          if (!formatted[m]) formatted[m] = { menuPermissions: [], screens: [], fields: [] };
          if (perm.permissionTypeName === 'Menu') formatted[m].menuPermissions.push({ ...perm });
          else if (perm.permissionTypeName === 'Screen') formatted[m].screens.push({ ...perm });
          else if (perm.permissionTypeName === 'Field') formatted[m].fields.push({ ...perm });
        });
        Object.values(formatted).forEach((mod) => {
          mod.menuPermissions.forEach((menu) => {
            menu.screenPermissions = [];
            mod.screens.forEach((screen) => {
              if (screen.permissionParentId === menu.permissionId) {
                screen.fieldPermissions = [];
                mod.fields.forEach((field) => { if (field.permissionParentId === screen.permissionId) screen.fieldPermissions.push(field); });
                menu.screenPermissions.push(screen);
              }
            });
          });
        });
        Object.keys(formatted).forEach((m) => { delete formatted[m].screens; delete formatted[m].fields; });
        setCatalogue(formatted);
      })
      .catch((e) => console.error('getUiPermissions', e));
  }, []);

  /* merge the selected role's UI perms into the catalogue */
  useEffect(() => {
    if (!selectedRoleId || !catalogue) return;
    setLoading(true);
    apiServices
      .getUiPermissionsByRoleId(selectedRoleId)
      .then((res) => {
        const roleUi = res.data?.roleUiPermissions || [];
        const byId = {};
        roleUi.forEach((p) => { byId[p.permissionId] = p; });
        const clone = JSON.parse(JSON.stringify(catalogue));
        Object.values(clone).forEach((mod) => {
          mod.menuPermissions.forEach((menu) => {
            applyRolePerm(menu, byId[menu.permissionId]);
            (menu.screenPermissions || []).forEach((sc) => {
              applyRolePerm(sc, byId[sc.permissionId]);
              (sc.fieldPermissions || []).forEach((f) => applyRolePerm(f, byId[f.permissionId]));
            });
          });
        });
        setTree(clone);
      })
      .catch((e) => console.error('getUiPermissionsByRoleId', e))
      .finally(() => setLoading(false));
  }, [selectedRoleId, catalogue]);

  // refresh the logged-in user's own permissions after a save (role-based UI visibility)
  useEffect(() => {
    if (!accountDetail || !isRoleChange) return;
    apiServices
      .getUserDetail(accountDetail.localAccountId)
      .then((res) => {
        if (res.status !== 200) return;
        setIsRoleChange(false);
        const user = res.data.user;
        localStorage.setItem('userInformation', JSON.stringify(user));
        dispatch(updateUserDetail({ userInformation: user }));
      })
      .catch((e) => console.error('getUserDetail', e));
  }, [isRoleChange, accountDetail, dispatch]);

  const cellOn = (moduleName, action) => {
    const field = UI_FIELD[action];
    if (!field || !tree) return false;
    const menu = topMenuOf(tree[moduleName]);
    return !!(menu && menu[field]);
  };

  const toggleCell = (moduleName, action) => {
    const field = UI_FIELD[action];
    if (!field) return; // Export has no backend equivalent
    setTree((prev) => {
      if (!prev?.[moduleName]) return prev;
      const clone = JSON.parse(JSON.stringify(prev));
      const menu = topMenuOf(clone[moduleName]);
      if (!menu) return prev;
      const next = !menu[field];
      menu[field] = next;
      // turning View off cascades to the module's screens/fields
      if (field === 'canView' && !next) {
        (menu.screenPermissions || []).forEach((sc) => {
          sc.canView = false;
          (sc.fieldPermissions || []).forEach((f) => { f.canView = false; f.canEdit = false; });
        });
      }
      return clone;
    });
  };

  const saveChanges = () => {
    if (!tree || !selectedRoleId) return;
    setSaving(true);
    apiServices
      .saveUiPermissionsByRole(buildUiSaveFormat(tree, selectedRoleId))
      .then((res) => {
        if (res.status === 200) {
          setIsRoleChange(true);
          toast.success('Permissions updated successfully!', { position: 'top-right' });
        }
      })
      .catch((err) => toast.error(`${err.message}`, { position: 'top-right' }))
      .finally(() => setSaving(false));
  };

  const scope = scopeByRole[selectedRoleId];
  const caps = capsByRole[selectedRoleId] || {};

  return (
    <div className="gos-set-role-layout">
      <aside>
        <div className="gos-set-row spread role-heading">
          <h2>Roles</h2>
          <button type="button" className="gos-set-btn soft small">
            <Icon name="plus" size={13} />
            New
          </button>
        </div>

        <div className="gos-set-role-list">
          {roles.map((role) => {
            const count = memberCount(role.id);
            return (
              <button
                type="button"
                key={role.id}
                className={`gos-set-role-item${role.id === selectedRoleId ? ' active' : ''}`}
                onClick={() => setSelectedRoleId(role.id)}
              >
                <span className="gos-set-role-dot" style={{ background: role.color }} />
                <span className="gos-set-role-copy">
                  <strong>{role.name}</strong>
                  <small>{count} {count === 1 ? 'member' : 'members'}</small>
                </span>
                {isSystemRole(role) && <span className="gos-set-badge gray">System</span>}
              </button>
            );
          })}
          {!roles.length && <div className="gos-set-muted" style={{ padding: '8px 2px' }}>Loading roles…</div>}
        </div>
      </aside>

      {selectedRole && (
        <div className="gos-set-card gos-set-card-pad">
          <div className="gos-set-role-detail-head">
            <div>
              <div className="gos-set-role-title">
                <span className="gos-set-role-dot large" style={{ background: selectedRole.color }} />
                <h2>{selectedRole.name}</h2>
                {locked && <span className="gos-set-badge gray">Locked</span>}
              </div>
              <p>{roleDescOf(selectedRole)}</p>
            </div>
            {!locked && (
              <div className="gos-set-row">
                <button type="button" className="gos-set-btn ghost small">
                  <Icon name="edit" size={13} />
                  Rename
                </button>
                <button type="button" className="gos-set-icon-btn danger" aria-label="Delete role">
                  <Icon name="trash" size={15} />
                </button>
              </div>
            )}
          </div>

          {locked && (
            <div className="gos-set-note">
              <Icon name="info" size={15} />
              The {selectedRole.name} role always has full access and cannot be edited.
            </div>
          )}

          <section className="gos-set-perm-section">
            <h3>Module access</h3>
            <p>What this role can do in each part of the workspace.{loading ? ' · Loading…' : ''}</p>
            <div className="gos-set-matrix-wrap">
              <table className="gos-set-matrix">
                <thead>
                  <tr>
                    <th className="module">Module</th>
                    {ACTIONS.map((action) => (
                      <th key={action}>{ACTION_LABEL[action]}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {modules.map((moduleName) => (
                    <tr key={moduleName}>
                      <td className="module">
                        <span className="gos-set-module-name">
                          <span className="gos-set-module-icon">
                            <Icon name={moduleIconName(moduleName)} size={16} />
                          </span>
                          {moduleName}
                        </span>
                      </td>
                      {ACTIONS.map((action) => {
                        const noBackend = !UI_FIELD[action]; // Export
                        const disabled = noBackend || locked;
                        const enabled = cellOn(moduleName, action);
                        return (
                          <td key={action}>
                            <button
                              type="button"
                              className={`gos-set-perm-cell${enabled ? ' on' : ''}${disabled ? ' disabled' : ''}`}
                              onClick={() => !disabled && toggleCell(moduleName, action)}
                              aria-label={`${ACTION_LABEL[action]} ${moduleName}`}
                              disabled={disabled}
                            >
                              <Icon name="check" size={14} />
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {!modules.length && (
                    <tr><td className="module" colSpan={ACTIONS.length + 1} style={{ color: 'var(--gos-text-3)' }}>No modules found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="gos-set-perm-section">
            <h3>Record visibility</h3>
            <p>The default set of records this role can see.</p>
            <div className="gos-set-scope-grid">
              {SCOPES.map((s) => (
                <button
                  type="button"
                  key={s.id}
                  className={`gos-set-scope-card${scope === s.id ? ' active' : ''}${locked ? ' disabled' : ''}`}
                  onClick={() => !locked && setScopeByRole((prev) => ({ ...prev, [selectedRoleId]: s.id }))}
                  disabled={locked}
                >
                  <span className="gos-set-scope-icon">
                    <Icon name={s.icon} size={17} />
                  </span>
                  <span>
                    <strong>{s.label}</strong>
                    <small>{s.desc}</small>
                  </span>
                </button>
              ))}
            </div>
          </section>

          <section className="gos-set-perm-section">
            <h3>Administration</h3>
            <p>Elevated capabilities that apply across the whole workspace.</p>
            {ADMIN_CAPS.map((cap) => (
              <div className="gos-set-cap-row" key={cap.id}>
                <span className="gos-set-cap-icon">
                  <Icon name={cap.icon} size={16} />
                </span>
                <span className="gos-set-cap-copy">
                  <strong>{cap.label}</strong>
                  <small>{cap.desc}</small>
                </span>
                <Switch
                  on={!!caps[cap.id]}
                  disabled={locked}
                  onClick={() =>
                    setCapsByRole((prev) => ({
                      ...prev,
                      [selectedRoleId]: { ...(prev[selectedRoleId] || {}), [cap.id]: caps[cap.id] ? 0 : 1 },
                    }))
                  }
                />
              </div>
            ))}
          </section>

          {!locked && (
            <div className="gos-set-actions">
              <button type="button" className="gos-set-btn primary" onClick={saveChanges} disabled={saving || !tree}>
                <Icon name="check" size={16} />
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MembersTab() {
  const [members, setMembers] = useState(MEMBERS_INIT);
  const [query, setQuery] = useState('');
  const roles = useMemo(createRoles, []);
  const visibleMembers = members.filter((member) => {
    const search = query.toLowerCase();
    return member.name.toLowerCase().includes(search) || member.email.toLowerCase().includes(search);
  });

  return (
    <div className="gos-set-card overflow-visible">
      <div className="gos-set-card-head">
        <div className="gos-set-card-title">
          <Icon name="leads" size={16} />
          Team Members
          <span className="gos-set-count">{members.length}</span>
        </div>
        <button type="button" className="gos-set-btn soft small">
          <Icon name="plus" size={13} />
          Invite Member
        </button>
      </div>

      <div className="gos-set-search-row">
        <label className="gos-set-search">
          <Icon name="search" size={15} />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search members..." />
        </label>
      </div>

      <div className="gos-set-table-wrap">
        <table className="gos-set-table">
          <thead>
            <tr>
              <th>Member</th>
              <th>Role</th>
              <th>Team</th>
              <th>Status</th>
              <th>Last active</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {visibleMembers.map((member) => (
              <tr key={member.id}>
                <td>
                  <span className="gos-set-person-cell">
                    <Avatar name={member.name} color={member.color} size={34} />
                    <span>
                      <strong>{member.name}</strong>
                      <small>{member.email}</small>
                    </span>
                  </span>
                </td>
                <td>
                  <RoleName roleId={member.role} roles={roles} />
                </td>
                <td>{member.team}</td>
                <td>
                  <span className={`gos-set-badge ${member.status === 'active' ? 'green' : 'amber'}`}>
                    {member.status === 'active' ? 'Active' : 'Invited'}
                  </span>
                </td>
                <td>{member.last}</td>
                <td>
                  <button
                    type="button"
                    className="gos-set-icon-btn"
                    aria-label={`Remove ${member.name}`}
                    onClick={() => setMembers((items) => items.filter((item) => item.id !== member.id))}
                  >
                    <Icon name="x" size={15} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function HierarchyTab() {
  const [teams, setTeams] = useState(TEAMS_INIT);

  return (
    <div className="gos-set-card gos-set-card-pad">
      <div className="gos-set-row spread wrap">
        <div>
          <div className="gos-set-card-title larger">
            <Icon name="accounts" size={17} />
            Team Hierarchy
          </div>
          <div className="gos-set-muted">Plan roles and reporting lines visually.</div>
        </div>
        <button
          type="button"
          className="gos-set-btn soft small"
          onClick={() =>
            setTeams((items) => [
              ...items,
              {
                id: `team-${items.length + 1}`,
                name: 'New Team',
                color: '#54738f',
                manager: { name: 'New Lead', role: 'Team Lead', color: '#54738f' },
                members: [],
              },
            ])
          }
        >
          <Icon name="plus" size={13} />
          Add Team
        </button>
      </div>

      <div className="gos-set-team-board">
        {teams.map((team) => (
          <div className="gos-set-team-col" key={team.id}>
            <div className="gos-set-team-bar" style={{ background: team.color }} />
            <div className="gos-set-team-top">
              <span>
                <strong>{team.name}</strong>
                <small>{team.members.length + 1} {team.members.length === 0 ? 'member' : 'members'}</small>
              </span>
              <button
                type="button"
                className="gos-set-icon-btn"
                aria-label={`Delete ${team.name}`}
                onClick={() => setTeams((items) => items.filter((item) => item.id !== team.id))}
              >
                <Icon name="trash" size={15} />
              </button>
            </div>

            <div className="gos-set-team-lead">
              <Avatar name={team.manager.name} color={team.manager.color} size={36} />
              <span>
                <strong>{team.manager.name}</strong>
                <small>{team.manager.role}</small>
              </span>
              <span className="gos-set-badge purple">Lead</span>
            </div>

            {team.members.map((member, index) => (
              <div className="gos-set-team-member" key={`${member.name}-${index}`}>
                <Avatar name={member.name} color={member.color} size={32} />
                <span>
                  <strong>{member.name}</strong>
                  <small>{member.role}</small>
                </span>
                <button
                  type="button"
                  className="gos-set-icon-btn"
                  aria-label={`Remove ${member.name}`}
                  onClick={() =>
                    setTeams((items) =>
                      items.map((item) =>
                        item.id === team.id
                          ? { ...item, members: item.members.filter((_, memberIndex) => memberIndex !== index) }
                          : item
                      )
                    )
                  }
                >
                  <Icon name="x" size={14} />
                </button>
              </div>
            ))}

            <button
              type="button"
              className="gos-set-team-add"
              onClick={() =>
                setTeams((items) =>
                  items.map((item) =>
                    item.id === team.id
                      ? {
                          ...item,
                          members: [...item.members, { name: 'New Member', role: 'Account Executive', color: team.color }],
                        }
                      : item
                  )
                )
              }
            >
              <Icon name="plus" size={14} />
              Add member
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function TerritoriesTab() {
  const totalAccounts = TERRITORIES.reduce((sum, territory) => sum + territory.accounts, 0);

  return (
    <div>
      <div className="gos-set-card gos-set-info-card">
        <div className="gos-set-card-head">
          <div className="gos-set-card-title">
            <Icon name="globe" size={16} />
            Territories
            <span className="gos-set-count">{TERRITORIES.length}</span>
          </div>
          <button type="button" className="gos-set-btn soft small">
            <Icon name="plus" size={13} />
            Add Territory
          </button>
        </div>
        <div className="gos-set-inline-note">
          <Icon name="info" size={14} />
          <span>{totalAccounts.toLocaleString()} accounts routed across {TERRITORIES.length} territories.</span>
        </div>
      </div>

      <div className="gos-set-territory-grid">
        {TERRITORIES.map((territory) => (
          <div className="gos-set-territory-card" key={territory.id}>
            <div className="gos-set-territory-top">
              <span className="gos-set-territory-icon" style={{ background: territory.color }}>
                <Icon name="globe" size={20} />
              </span>
              <span className="grow">
                <span className="gos-set-row spread">
                  <strong>{territory.name}</strong>
                  <span className="gos-set-tag">{territory.region}</span>
                </span>
                <small>{territory.states}</small>
              </span>
            </div>
            <div className="gos-set-territory-foot">
              <span className="gos-set-person-cell compact">
                <Avatar name={territory.lead} color={territory.leadColor} size={26} />
                <span>
                  <strong>{territory.lead}</strong>
                  <small>{territory.team}</small>
                </span>
              </span>
              <span className="gos-set-stat">
                <strong>{territory.accounts}</strong>
                <small>accounts</small>
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PermissionsSection() {
  const [tab, setTab] = useState('roles');
  const tabs = [
    { id: 'roles', label: 'Roles & Permissions', icon: 'settings' },
    { id: 'members', label: 'Members', icon: 'leads' },
    { id: 'hierarchy', label: 'Team Hierarchy', icon: 'accounts' },
    { id: 'territories', label: 'Territories', icon: 'globe' },
  ];

  return (
    <section className="gos-set-fade">
      <header className="gos-set-head">
        <div>
          <h1>Teams & Permissions</h1>
          <p>Structure roles, territories, and ownership while controlling access.</p>
        </div>
      </header>

      <div className="gos-set-tabs">
        {tabs.map((item) => (
          <button
            type="button"
            key={item.id}
            className={`gos-set-tab${tab === item.id ? ' active' : ''}`}
            onClick={() => setTab(item.id)}
          >
            <Icon name={item.icon} size={15} />
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'roles' && <RolesTab />}
      {tab === 'members' && <MembersTab />}
      {tab === 'hierarchy' && <HierarchyTab />}
      {tab === 'territories' && <TerritoriesTab />}
    </section>
  );
}

function StubSection({ item }) {
  return (
    <section className="gos-set-fade">
      <header className="gos-set-head">
        <div>
          <h1>{item.label}</h1>
          <p>Coming soon to GrowthOS.</p>
        </div>
      </header>
      <div className="gos-set-card gos-set-stub">
        <span className="gos-set-stub-icon">
          <Icon name={item.icon} size={24} />
        </span>
        <strong>{item.label}</strong>
        <p>This settings module is on the roadmap. General and Teams & Permissions are ready to explore now.</p>
      </div>
    </section>
  );
}

const GrowthOSSettings = () => {
  const [section, setSection] = useState('permissions');
  const navigate = useNavigate();
  const activeItem = NAV.find((item) => item.id === section) || NAV.find((item) => item.id === 'permissions');

  const renderContent = () => {
    if (section === 'general') return <GeneralSection />;
    if (section === 'permissions') return <PermissionsSection />;
    if (section === 'personal') return <PersonalSettings embedded />;
    if (section === 'audit') return <AuditLog embedded />;
    if (section === 'teams') return <TeamManagement embedded />;
    if (section === 'buckets') return <BucketManagement embedded />;
    if (section === 'bucketlist') return <BucketList embedded />;
    if (section === 'actionperms') return <ActionPermissionList embedded />;
    if (section === 'uiperms') return <UiPermissionsList embedded />;
    if (section === 'rolemgmt') return <RoleManagement embedded />;
    if (section === 'users') return <UserManagementList embedded />;
    return <StubSection item={activeItem} />;
  };

  return (
    <div className="inner-page gos-settings-page">
      <div className="gos-settings">
        <nav className="gos-set-nav" aria-label="Settings sections">
          {NAV.map((item, index) =>
            item.group ? (
              <div className="gos-set-nav-group" key={`${item.group}-${index}`}>
                {item.group}
              </div>
            ) : (
              <button
                type="button"
                key={item.id || item.path}
                className={`gos-set-nav-item${section === item.id ? ' active' : ''}${item.soon ? ' disabled' : ''}`}
                onClick={() => {
                  if (item.soon) return;
                  if (item.path) navigate(item.path);
                  else setSection(item.id);
                }}
                disabled={item.soon}
              >
                <Icon name={item.icon} size={17} />
                <span>{item.label}</span>
                {item.soon && <span className="gos-set-soon">Soon</span>}
              </button>
            )
          )}
        </nav>

        <main className="gos-set-content">{renderContent()}</main>
      </div>
    </div>
  );
};

export default GrowthOSSettings;
